
import {
    FooterAction,
    HeaderAction,
    HeaderActionsContainer,
    HeaderTitle,
    HeaderTitleContainer,
    HeaderTitleIcon,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from '../../../../CommonComponents/modals/Modal'
import './CreationModal.scss'
import useNavigation from '../../hooks/useNavigation'
import { Fragment, useEffect, useMemo, useState } from 'react'
import ScenarioSteps from '../scenario-steps/ScenarioSteps'
import { CloseSvg, ScenarioLogo, ToggleMinimizeSvg } from '../../../../CommonComponents/svgs/Svgs'
import useFormSubmit from '../../hooks/useFormSubmit'
import useLoader from '../../hooks/useLoader'
import useForm from '../../hooks/useForm'
import { getBackButtonText, getConfirmButtonText, goPreviousStep } from '../../utils/helperFunction'
import useStepper from '../../hooks/useStepper'
import { DialogueModalType } from '../../utils/scenario-types'
import useModal from '../../hooks/useModal'
import useStepApi from '../../hooks/useStepApi'
import useOrbitAnimationDetails from '../../hooks/useOrbitAnimationDetails'
import useScenarioResponse from '../../hooks/useScenarioResponse'
import useViewer from '../../hooks/useViewer'
import { clearViewer, plotScenarioElements } from '../../utils/viewer-packets'
import { GroundStationSvg, MissileSvg } from '../../../../modelling/Svgs'
import { SatelliteSvg } from '../../../scenario/svgs/Svgs'
import { SCENARIO_STEPS } from '../../../constants'
import CustomToolTip from '../../../../CommonComponents/CustomToolTip'
import { truncateName } from '../../../../../utils/commonFunctions'
import { formatElementType } from '../scenario-steps/details/ScenarioDetails'
import CustomToast from '../../../../CommonComponents/toast/CustomToast'
import {
    getScenarioSetupMessage,
    getScenarioSetupStatus,
    isOnboardScheduleGenerateRunning,
    isSatelliteGroupsAddRunning
} from '../../utils/satellite-groups-add'

const getExecutionProgressMessage = (loader: string | boolean, message?: string | false) => {
    return (loader === 'Plotting' || loader === 'Generating') && message ? message : ''
}

const CreationModal = () => {
    const { formData, scenarioJson, selectedSimulator } = useForm()
    const { viewer } = useViewer()
    const { loading } = useLoader()
    const { step } = useNavigation()
    const { onSubmit } = useFormSubmit()
    const { undeployScenario } = useStepApi()
    const { updateScenarioStep } = useStepper()
    const { setModals, onCloseScenario } = useModal()
    const { scenarioResponse } = useScenarioResponse()
    const { orbitAnimationDetails } = useOrbitAnimationDetails()

    const [expand, setExpand] = useState<boolean>(true)
    const loader = useMemo(() => loading.execution, [loading])
    const setupStatus = useMemo(() => getScenarioSetupStatus(scenarioResponse), [scenarioResponse])
    const satelliteGroupsAddRunning = useMemo(
        () => isSatelliteGroupsAddRunning(setupStatus),
        [setupStatus]
    )
    const scheduleGenerationRunning = useMemo(
        () => formData.assets.schedule.status === 'running' ||
            isOnboardScheduleGenerateRunning(setupStatus),
        [formData.assets.schedule.status, setupStatus]
    )
    const effectiveLoader = useMemo(() => (
        loader || (satelliteGroupsAddRunning ? 'Plotting' : scheduleGenerationRunning ? 'Generating' : false)
    ), [loader, satelliteGroupsAddRunning, scheduleGenerationRunning])
    const effectiveExecutionMessage = useMemo(
        () => loading.executionMessage || getScenarioSetupMessage(scenarioResponse) || false,
        [loading.executionMessage, scenarioResponse]
    )
    const progressMessage = useMemo(() => (
        getExecutionProgressMessage(effectiveLoader, effectiveExecutionMessage)
    ), [effectiveLoader, effectiveExecutionMessage])
    const undeployLoader = useMemo(() => loading.undeploy, [loading])
    const orbitExpandedView = useMemo(() => orbitAnimationDetails.expandedView, [orbitAnimationDetails.expandedView])
    const animationRunning = useMemo(() => orbitAnimationDetails.startAnimation, [orbitAnimationDetails.startAnimation])

    const isManualDeployDisabled = useMemo(() => {
        const isManualMode = formData.details.scenarioOperation === 'Manual'
        if (!isManualMode || step !== SCENARIO_STEPS.DETAILS) return false
        if (!formData.details.scenarioName?.trim()) return true
        if (!scenarioJson) return true
        const elementTypes = Array.from(new Set(
            scenarioJson.elements?.map((el: any) => el?.element_config?.element_type).filter(Boolean)
        )) as string[]
        if (elementTypes.length === 0) return true
        return elementTypes.some((type) => !selectedSimulator[type])
    }, [formData.details.scenarioOperation, formData.details.scenarioName, step, scenarioJson, selectedSimulator])

    // `loading.base` and `loading.execution` may be strings (loader status), so
    // coerce to a boolean before passing to components which expect `boolean | undefined`.
    const disabled = Boolean(
        loading.base ||
        effectiveLoader ||
        animationRunning ||
        isManualDeployDisabled ||
        undeployLoader
    )

    const [groupedElements, setGroupedElements] = useState<any>(null)

    const close = () => {
        return onCloseScenario('Dashboard')
    }

    useEffect(() => {
        if (!animationRunning) {
            setExpand(true)
        } else {
            setExpand(false)
        }
    }, [animationRunning])

    const undeploy = () => {
        const modalDetails = {
            type: 'delete' as DialogueModalType['type'],
            title: 'Undeploy Scenario?',
            description: 'Undeploying this scenario will stop the simulation',
            onConfirm: () => {
                undeployScenario()
                setModals((prev) => ({
                    ...prev,
                    dialouge: null
                }))
            },
            onCancel: () => {
                setModals((prev) => ({
                    ...prev,
                    dialouge: null
                }))
            },
            confirm_button_text: 'Confirm',
        }
        setModals((prev) => ({
            ...prev,
            dialouge: modalDetails
        }))
    }

    useEffect(() => {
        if (viewer && scenarioJson) {
            clearViewer(viewer)
            plotScenarioElements(viewer, scenarioJson)

            const groupedElement = scenarioJson.elements.reduce((acc: any, element: any) => {
                const elementType = element?.element_config?.element_type || 'unknown';
                if (!acc[elementType]) {
                    acc[elementType] = [];
                }
                acc[elementType].push(element);
                return acc;
            }, {});
            setGroupedElements(groupedElement)
        }
    }, [viewer, scenarioJson])

    if (orbitExpandedView) return null

    return (
        <Fragment>
            <Modal open={true} pinned={true} disabled={true} className={`scenario__create__modal ${expand ? 'expanded' : ''}`}>
                <ModalHeader>
                    <HeaderTitleContainer>
                        <HeaderTitleIcon>
                            <ScenarioLogo />
                        </HeaderTitleIcon>
                        <HeaderTitle>
                            Scenario Creation
                        </HeaderTitle>
                    </HeaderTitleContainer>
                    <HeaderActionsContainer>
                        <HeaderAction title={expand ? 'Minimize' : 'Maximize'} onClick={() => setExpand(!expand)}>
                            <ToggleMinimizeSvg type={expand ? 'minimize' : 'maximize'} />
                        </HeaderAction>
                        <HeaderAction title='Close' onClick={() => onCloseScenario('Dashboard')}>
                            <CloseSvg />
                        </HeaderAction>
                    </HeaderActionsContainer>
                </ModalHeader>
                <ModalBody>
                    <ScenarioSteps />
                </ModalBody>
                <ModalFooter>
                    <FooterAction variant='outline__button'
                        disabled={disabled}
                        onClick={() => { goPreviousStep(step as string, formData, updateScenarioStep, close, undeploy) }}
                    >
                        {getBackButtonText(step as string, effectiveLoader || undeployLoader)}
                        {undeployLoader && <div className='spin__loader' />}
                    </FooterAction>
                    <FooterAction variant='primary__button'
                        disabled={disabled}
                        onClick={onSubmit}
                    >
                        {getConfirmButtonText(step as string, effectiveLoader, formData, scenarioResponse)}
                        {effectiveLoader && <div className='spin__loader' />}
                    </FooterAction>
                </ModalFooter>
            </Modal>
            <CustomToast type='loading' show={Boolean(progressMessage)} message={progressMessage} />
            {scenarioJson &&
                <div className='scenario_uploaded_objects'>
                    <div className='object_header'>
                        <span>
                            {`Elements (${scenarioJson?.elements?.length || 0})`}
                        </span>
                    </div>

                    {(scenarioJson?.elements?.length > 0 && groupedElements) ? (
                        <Fragment>
                            {Object.entries(groupedElements).map(([elementType, elements]: any) => (
                                <div key={elementType}>
                                    {/* Entity Type Header */}
                                    <div className='sub_object_header'>
                                        <span>{formatElementType(elementType)}</span>
                                    </div>

                                    {/* Entity List */}
                                    {elements.map((entity: any) => (
                                        <div
                                            key={entity.element_name}
                                            className='sub_object_elements'
                                        >
                                            {/* Icon conditionally */}
                                            {elementType === 'satellite' && <SatelliteSvg />}
                                            {elementType === 'groundstation' && <GroundStationSvg />}
                                            {elementType === 'radars' && <GroundStationSvg />}
                                            {(elementType === 'interceptors' || elementType === 'missile') && <MissileSvg />}
                                            <CustomToolTip title={entity.element_name || ''} placement='top'>
                                                <span>{truncateName(entity.element_name, 12, 6, 6)}</span>
                                            </CustomToolTip>
                                        </div>
                                    ))}
                                </div>
                            ))
                            }
                        </Fragment>
                    ) : (
                        <div className='no_elements'>No elements in scenario</div>
                    )}
                </div>
            }
        </Fragment>
    )
}

export default CreationModal
